import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { BaseApiService } from '@/services/BaseApiService'
import { accountApiService } from '@/services/accountApi'
import { AUTH_TOKEN_KEY, AUTH_TOKEN_EXPIRY_KEY, AUTH_REFRESH_TOKEN_KEY, AUTH_REFRESH_TOKEN_EXPIRY_KEY, TOKEN_REFRESH_CHECK_INTERVAL_MS } from '@/constants/auth'

interface AuthState {
  isAuthenticated: boolean
  token: string | null
  refreshToken: string | null
  expiresAt: string | null
}

interface AuthContextValue extends AuthState {
  logout: () => void
  refreshAuthState: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function getAuthStateFromStorage(): AuthState {
  if (typeof window === 'undefined') {
    return { isAuthenticated: false, token: null, refreshToken: null, expiresAt: null }
  }

  const token = localStorage.getItem(AUTH_TOKEN_KEY)
  const refreshToken = localStorage.getItem(AUTH_REFRESH_TOKEN_KEY)
  const expiresAt = localStorage.getItem(AUTH_TOKEN_EXPIRY_KEY)

  return {
    isAuthenticated: !!token && !!refreshToken && !!expiresAt && new Date(expiresAt) > new Date(),
    token,
    refreshToken,
    expiresAt,
  }
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>(getAuthStateFromStorage)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const refreshAuthState = useCallback(() => {
    setAuthState(getAuthStateFromStorage())
  }, [])

  const logout = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(AUTH_TOKEN_EXPIRY_KEY)
    localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)
    localStorage.removeItem(AUTH_REFRESH_TOKEN_EXPIRY_KEY)

    // Update React state
    setAuthState({ isAuthenticated: false, token: null, refreshToken: null, expiresAt: null })

    // Clear React Query cache
    queryClient.clear()

    // Navigate to login
    navigate({ to: '/login', replace: true })
  }, [queryClient, navigate])

  // Register the logout handler with BaseApiService for 401 responses
  useEffect(() => {
    BaseApiService.setLogoutHandler(logout)

    return () => {
      BaseApiService.setLogoutHandler(null)
    }
  }, [logout])

  // Listen for storage events (in case of logout in another tab)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key === AUTH_TOKEN_KEY ||
        event.key === AUTH_REFRESH_TOKEN_KEY
      ) {
        refreshAuthState()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [refreshAuthState])

  // Check auth state on mount and when window gains focus
  useEffect(() => {
    refreshAuthState()

    const handleFocus = () => refreshAuthState()
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [refreshAuthState])

  // Proactive token refresh - check every minute and immediately on visibility change
  useEffect(() => {
    if (!authState.isAuthenticated) {
      return
    }

    const checkAndRefresh = async () => {
      if (accountApiService.getTimeUntilRefresh() <= 0) {
        const success = await accountApiService.refreshToken()
        if (success) refreshAuthState()
      }
    }

    checkAndRefresh()
    const interval = setInterval(checkAndRefresh, TOKEN_REFRESH_CHECK_INTERVAL_MS)

    // Also refresh immediately when the tab becomes visible after being hidden
    // (e.g. device woke from sleep, user switched tabs for a long time)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkAndRefresh()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [authState.isAuthenticated, refreshAuthState])

  return (
    <AuthContext.Provider value={{ ...authState, logout, refreshAuthState }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

